
import React from 'react';

const ComprehensiveApproachSection: React.FC = () => {
  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              A Comprehensive Approach to Industrial Hygiene and Safety
            </h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              HP Inc Consultant Services takes a meticulous and comprehensive approach to ensure workplace health and safety through its industrial hygiene services. The process involves several key steps, from initial assessment to implementing changes, training workers, and ongoing facility monitoring.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Initial Facility Assessment and Dialogue
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    The process begins with an in-depth evaluation of the facility. We engage in detailed discussions with clients to understand their operations, chemicals used, and potential employee exposures. By reviewing facility maps and discussing specific activities like welding, painting, or grinding, consultants can pinpoint areas of concern.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-green-600 font-bold">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Review of Safety Data Sheets and Identification of Hazards
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    The assessment phase involves reviewing safety data sheets for all chemicals used within the facility to determine which substances require monitoring and to understand where respiratory protection might be needed.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-purple-600 font-bold">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Conducting On-Site Monitoring
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Following the initial assessment, we schedule a day to conduct on-site monitoring. Our industrial hygiene consultants use specialized equipment to monitor air quality and noise levels, focusing on areas and employees identified during the assessment phase.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-orange-600 font-bold">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Analysis of Results and Report Generation
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Once we complete monitoring, the collected data is analyzed directly or sent to a laboratory for processing. We then compile a comprehensive report detailing individual and area-specific results with recommendations for mitigating exposure to identified hazards.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-red-600 font-bold">5</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Development and Implementation of Corrective Action Plans
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Based on the findings of the monitoring report, we work with clients to develop and implement corrective action plans. These plans may involve installing new ventilation systems, adjusting work processes to minimize exposure, or introducing specific types of PPE.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-indigo-600 font-bold">6</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Worker Training and Education
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    An essential component of the corrective action plan involves training and educating employees about the identified hazards, the importance of the implemented changes, and how to use PPE correctly. We provide comprehensive training sessions covering exposure-related health implications.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                  <span className="text-teal-600 font-bold">7</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Ongoing Monitoring and Reassessment
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    We recommend ongoing facility monitoring to ensure the continued effectiveness of implemented changes. This work may include regular air and noise monitoring to assess the impact of new equipment or changes in production processes.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
                <h4 className="font-semibold text-blue-800 mb-3">
                  Why Choose Our Comprehensive Approach?
                </h4>
                <ul className="text-blue-700 space-y-2">
                  <li className="flex items-start">
                    <i className="ri-check-line mr-2 mt-1 flex-shrink-0"></i>
                    <span>Tailored solutions for your specific industry and operations</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-2 mt-1 flex-shrink-0"></i>
                    <span>Regulatory compliance and risk management expertise</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-check-line mr-2 mt-1 flex-shrink-0"></i>
                    <span>Ongoing support and continuous improvement</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-16 text-center">
            <a 
              href="#contact-form" 
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 whitespace-nowrap cursor-pointer text-lg"
            >
              Start Your Comprehensive Assessment
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ComprehensiveApproachSection;
